import { parseGitHubPrUrl } from './github-url.js';
import { parseGitLabMrUrl } from './gitlab-url.js';

/**
 * Provider-neutral façade over the per-provider PR/MR URL parsers. This is
 * what lets provider-neutral chips (`PullRequestChip`) render either
 * provider's URL without knowing which host they were handed;
 * provider-specific code should keep using the per-provider parsers, whose
 * shapes carry the provider's own vocabulary (owner/repo vs. project path).
 */

export type GitProvider = 'github' | 'gitlab';

/** Display names for the providers, as their owners spell them. */
export const GIT_PROVIDER_LABELS: Record<GitProvider, string> = {
  github: 'GitHub',
  gitlab: 'GitLab',
};

/** A pull/merge request reference in the shape a provider-neutral chip needs. */
export interface PrUrlRef {
  provider: GitProvider;
  /** `owner/repo` on GitHub; the full (possibly nested) project path on GitLab. */
  repoSlug: string;
  /** The PR number (GitHub) or MR iid (GitLab). */
  number: number;
  /** Provider-native singular name used in accessible and fallback copy. */
  requestLabel: 'pull request' | 'merge request';
  /** Provider-native reference marker (`#42` on GitHub, `!42` on GitLab). */
  referencePrefix: '#' | '!';
  /** The repository's canonical web URL, for a link to the repo itself. */
  repoWebUrl: string;
}

/**
 * Extract the first pull-request or merge-request reference from a string, or
 * `null` when there is none. Tries GitHub first, then GitLab; each provider's
 * pattern only matches its own host, so order never changes the result.
 */
export function parsePrUrl(text: string): PrUrlRef | null {
  const github = parseGitHubPrUrl(text);
  if (github) {
    return {
      provider: 'github',
      repoSlug: `${github.owner}/${github.repo}`,
      number: github.number,
      requestLabel: 'pull request',
      referencePrefix: '#',
      repoWebUrl: `https://github.com/${github.owner}/${github.repo}`,
    };
  }
  const gitlab = parseGitLabMrUrl(text);
  if (gitlab) {
    return {
      provider: 'gitlab',
      repoSlug: gitlab.projectPath,
      number: gitlab.iid,
      requestLabel: 'merge request',
      referencePrefix: '!',
      repoWebUrl: `https://gitlab.com/${gitlab.projectPath}`,
    };
  }
  return null;
}
